import React from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../store';
import { ContactForm } from '../../common/contact-form';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import { CheckboxComponent } from 'n2p-ui-library';
import DateInput from '../../common/inputs/date-input';

const OnboardingInformation = observer(() => {
  const { t } = useTranslation('', { keyPrefix: 'ONBOARDING_INFORMATION' });
  const { t: tError } = useTranslation('', { keyPrefix: 'ONBOARDING_INFORMATION.ERRORS' });
  const {
    onboardingInformationStore: store,
    onboardingContactsStore: contactsStore,
    adminContactStore,
    quoteStore,
    shippingInformationStore,
    billingInformationStore,
    tenantStore,
  } = useStore();

  const { config } = tenantStore;

  const handleChange = (index: number, field: keyof (typeof contactsStore.contacts)[0], value: string) => {
    shippingInformationStore.uncheckSameAsFirstOnboardingContact();
    billingInformationStore.uncheckSameAsOnboardingContacts();
    store.updateContact(index, field, value);
  };

  const { targetInstallDate, targetPortDate } = store.information;
  const { contacts } = contactsStore;
  const { contacts: adminContacts } = adminContactStore;
  const { isContactOnlyPartner, isSelfOnboarding } = quoteStore;
  const title = isContactOnlyPartner ? 'TITLE_CONTACT_PARTNER' : 'TITLE';
  const info = isContactOnlyPartner ? 'INFO_CONTACT_PARTNER' : 'INFO';

  const renderDates = () => {
    if (config.useOnlyOneTargetDate) {
      return (
        <div className={'desired-start-date-wrapper'}>
          <DateInput
            date={targetInstallDate}
            label={t('DESIRED_START_DATE')}
            placeholder={t('DATE_PLACEHOLDER')}
            onUpdate={date => {
              store.updateTargetInstallDate(date);
              store.updateTargetPortDate(date);
            }}
            error={targetInstallDate?.errorType ? tError(`${targetInstallDate.errorType.toUpperCase()}_TARGET_INSTALL_DATE`) : ''}
          />
        </div>
      );
    }
    return (
      <div className={'container-two-columns'}>
        <DateInput
          date={targetInstallDate}
          label={t('TARGET_INSTALL_DATE')}
          placeholder={t('DATE_PLACEHOLDER')}
          onUpdate={date => {
            store.updateTargetInstallDate(date);
          }}
          error={targetInstallDate?.errorType ? tError(`${targetInstallDate.errorType.toUpperCase()}_TARGET_INSTALL_DATE`) : ''}
        />
        <DateInput
          date={targetPortDate}
          label={t('TARGET_PORT_DATE')}
          placeholder={t('DATE_PLACEHOLDER')}
          onUpdate={date => {
            store.updateTargetPortDate(date);
          }}
          error={targetPortDate?.errorType ? tError(`${targetPortDate.errorType.toUpperCase()}_TARGET_PORT_DATE`) : ''}
          optional={true}
          type={'port'}
        />
      </div>
    );
  };

  return (
    <div className={'step'}>
      <h2>{t(title)}</h2>
      <span className={'step-info'}>{t(info)}</span>

      {!isContactOnlyPartner &&
        contacts.map((contact, index) => (
          <ContactForm
            key={contact.uiKey}
            index={index}
            contact={contact}
            handleChange={(field, value) => handleChange(index, field, value)}
            removeContact={() => store.removeContact(index)}
            contactsType={'onboarding'}
          />
        ))}

      {!isContactOnlyPartner && contacts.length < 3 && (
        <button className={'n2pc-btn-text contact-add-btn'} onClick={() => store.addContact()}>
          {t('ADD_CONTACT')}
        </button>
      )}

      {!isContactOnlyPartner && <h3>{t('TARGET_DATES')}</h3>}
      {renderDates()}
      {isSelfOnboarding && (
        <>
          <h2>{t('ADMIN')}</h2>
          <span className={'step-info'}>{t('ADMIN_INFO')}</span>

          {!isContactOnlyPartner && (
            <div className={'container-same-as'}>
              <CheckboxComponent
                label={t('SAME_AS_FIRST_ONBOARDING_CONTACT')}
                onChange={e => store.updateSameAsOnboardingContact()}
                checked={store.isSameAsFirstOnboardingContact}
              />
            </div>
          )}

          {adminContacts.map((contact, index) => (
            <ContactForm
              key={contact.uiKey}
              index={index}
              contact={contact}
              handleChange={(field, value) => store.updateAdminContact(field, value)}
              contactsType={'admin'}
            />
          ))}
        </>
      )}
    </div>
  );
});

export default OnboardingInformation;
